import { useEffect, useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";
import { SHOTS } from "../data/content";

const FILTERS = [
  { id: "all", label: "All Shots", testid: "vfx-filter-all" },
  { id: "rotoscopy", label: "Rotoscopy", testid: "vfx-filter-rotoscopy" },
  { id: "prep", label: "Prep", testid: "vfx-filter-prep" },
  { id: "tracking", label: "Camera Tracking", testid: "vfx-filter-tracking" },
  { id: "rotomation", label: "Rotomation", testid: "vfx-filter-rotomation" },
  { id: "compositing", label: "Compositing", testid: "vfx-filter-compositing" },
];

const Vid = ({ mp4, webm, poster, vref }) => (
  <video ref={vref} className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline poster={poster} draggable={false}>
    <source src={mp4} type="video/mp4" />
    <source src={webm} type="video/webm" />
  </video>
);

const Slider = ({ shot }) => {
  const ref = useRef(null);
  const finalRef = useRef(null);
  const rawRef = useRef(null);
  const [pos, setPos] = useState(50);
  const [drag, setDrag] = useState(false);

  const syncRaw = () => {
    const f = finalRef.current, r = rawRef.current;
    if (f && r && Math.abs(f.currentTime - r.currentTime) > 0.1) r.currentTime = f.currentTime;
  };

  useEffect(() => {
    if (!shot.video || !shot.rawVideo) return;
    const f = finalRef.current;
    if (!f) return;
    f.addEventListener("timeupdate", syncRaw);
    f.addEventListener("seeked", syncRaw);
    return () => {
      f.removeEventListener("timeupdate", syncRaw);
      f.removeEventListener("seeked", syncRaw);
    };
  }, [shot]);

  const move = (clientX) => {
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
  };

  return (
    <div>
      <div ref={ref} data-testid={`slider-${shot.id}`}
        className="relative aspect-video overflow-hidden border border-white/10 select-none cursor-ew-resize"
        onPointerDown={(e) => { setDrag(true); e.target.setPointerCapture?.(e.pointerId); move(e.clientX); }}
        onPointerMove={(e) => drag && move(e.clientX)}
        onPointerUp={() => setDrag(false)}
        onPointerLeave={() => setDrag(false)}>
        {/* Final composite */}
        {shot.video
          ? <Vid vref={finalRef} mp4={shot.video} webm={shot.videoWebm} poster={shot.image} />
          : <img src={shot.image} alt={`${shot.title} final composite`} className="absolute inset-0 w-full h-full object-cover" draggable={false} />}
        {/* Raw plate (real plate if provided, otherwise simulated) */}
        <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
          {shot.rawVideo ? (
            <Vid vref={rawRef} mp4={shot.rawVideo} webm={shot.rawVideoWebm} poster={shot.image} />
          ) : (
            <>
              <img src={shot.rawImage || shot.image} alt={`${shot.title} raw plate`}
                className={`absolute inset-0 w-full h-full object-cover ${shot.rawImage ? "" : "grayscale contrast-75 brightness-110"}`} draggable={false} />
              {!shot.rawImage && (
                <>
                  <div className="absolute inset-0 bg-emerald-500/25 mix-blend-color" />
                  <div className="absolute inset-0 bg-emerald-400/10" />
                </>
              )}
            </>
          )}
        </div>
        {/* Handle */}
        <div data-testid="vfx-slider-handle" className="absolute top-0 bottom-0 w-px bg-amber-500" style={{ left: `${pos}%` }}>
          <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-amber-500 text-[#070709] flex items-center justify-center shadow-lg">
            <MoveHorizontal className="w-5 h-5" />
          </div>
        </div>
        <span className="absolute top-3 left-3 font-mono2 text-[10px] tracking-[0.25em] bg-black/60 px-2 py-1 text-emerald-300">RAW PLATE</span>
        <span className="absolute top-3 right-3 font-mono2 text-[10px] tracking-[0.25em] bg-black/60 px-2 py-1 text-amber-400">{shot.finalLabel || "FINAL COMP"}</span>
      </div>
      <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
        <h3 className="font-heading text-xl sm:text-2xl font-bold text-zinc-100">{shot.title}</h3>
        <div className="flex flex-wrap gap-x-5 gap-y-1 font-mono2 text-[10px] tracking-[0.15em] text-zinc-500 uppercase">
          <span>CAM <span className="text-zinc-300">{shot.meta.camera}</span></span>
          <span>ENGINE <span className="text-zinc-300">{shot.meta.render}</span></span>
          <span>FRAMES <span className="text-zinc-300">{shot.meta.frames}</span></span>
          <span>PASSES <span className="text-zinc-300">{shot.meta.passes}</span></span>
        </div>
      </div>
    </div>
  );
};

export const BeforeAfter = () => {
  const [filter, setFilter] = useState("all");
  const shots = SHOTS.filter((s) => filter === "all" || s.category === filter);

  return (
    <section id="breakdown" data-testid="vfx-comparison-section" className="py-24 px-4 sm:px-8 lg:px-16 bg-[#0E0E12]">
      <div className="max-w-7xl mx-auto">
        <p className="font-mono2 text-xs tracking-[0.35em] text-amber-500/90 mb-4">PLATE → PIXEL</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-wide text-zinc-50 mb-4">
          DRAG THE FRAME. <span className="text-stroke">SEE THE LIE.</span>
        </h2>
        <p className="max-w-xl text-zinc-400 mb-10">
          Every shot below ships with its raw plate. Drag the divider to compare what the camera saw against what the audience believed.
        </p>
        <div className="flex flex-wrap gap-2 mb-10">
          {FILTERS.map((f) => (
            <button key={f.id} data-testid={f.testid} onClick={() => setFilter(f.id)}
              className={`font-mono2 text-[11px] tracking-[0.2em] px-4 py-2 border transition-colors ${
                filter === f.id ? "border-amber-500 bg-amber-500 text-[#070709] font-semibold" : "border-white/15 text-zinc-400 hover:border-amber-500/60 hover:text-amber-400"
              }`}>
              {f.label.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {shots.map((s) => <Slider key={s.id} shot={s} />)}
        </div>
      </div>
    </section>
  );
};
